import { turnstileSize } from '../../shared/turnstile-size';

var CATEGORY_GROUP_LABELS = {
    pro: 'Udogodnienia',
    con: 'Uciążliwości',
    general: 'Obserwacje',
};

var MAX_ATTACHMENTS = 3;
var MAX_CLIENT_IMAGE_DIMENSION = 2048;
var CLIENT_WEBP_QUALITY = 0.84;
var MIN_RADIUS = 10;
var MAX_RADIUS = 5000;
var RADIUS_STEP = 10;

var STEP_PICK_POINT = 'pick_point';
var STEP_CHOOSE_TARGET = 'choose_target';
var STEP_CHOOSE_CATEGORY = 'choose_category';
var STEP_FORM = 'form';
var STEP_SUCCESS = 'success';

function isTrustedAuthor() {
    var user = (typeof window !== 'undefined') ? window.RCN_USER : null;
    return !!(user && user.emailVerified);
}

function getTrustedAuthorEmail() {
    var user = (typeof window !== 'undefined') ? window.RCN_USER : null;
    return (user && user.email) ? String(user.email) : '';
}

function defaultEsc(value) {
    return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function isFiniteNumber(value) {
    return typeof value === 'number' && isFinite(value);
}

function getStructuredTargetFieldName(targetType) {
    if (targetType === 'dzialka') return 'dzialka_id';
    if (targetType === 'budynek') return 'budynek_id';
    return '';
}

function replaceFileExtension(filename, extension) {
    var safeName = String(filename || 'image').trim();
    if (safeName === '') safeName = 'image';
    return safeName.replace(/\.[^.]+$/, '') + '.' + extension;
}

function formatRadiusLabel(meters) {
    if (meters < 1000) return meters + ' m';
    var km = meters / 1000;
    if (km >= 10) return Math.round(km) + ' km';
    return km.toFixed(1).replace(/\.0$/, '') + ' km';
}

function clampRadiusValue(value) {
    var radius = parseInt(value, 10);
    if (!isFinite(radius)) return 0;
    if (radius <= 0) return 0;
    if (radius < MIN_RADIUS) return MIN_RADIUS;
    if (radius > MAX_RADIUS) return MAX_RADIUS;
    return Math.round(radius / RADIUS_STEP) * RADIUS_STEP;
}

function normalizeRadiusValue(value) {
    var radius = clampRadiusValue(value);
    return radius > 0 ? String(radius) : '';
}

function loadImageElement(url) {
    return new Promise(function(resolve, reject) {
        var image = new Image();
        image.onload = function() { resolve(image); };
        image.onerror = function() { reject(new Error('Nie udało się wczytać obrazu.')); };
        image.decoding = 'async';
        image.src = url;
    });
}

function canvasToBlob(canvas, type, quality) {
    return new Promise(function(resolve, reject) {
        if (!canvas || typeof canvas.toBlob !== 'function') {
            reject(new Error('Przeglądarka nie obsługuje przygotowania podglądu obrazu.'));
            return;
        }
        canvas.toBlob(function(blob) {
            if (!blob) {
                reject(new Error('Nie udało się przygotować obrazu do wysyłki.'));
                return;
            }
            resolve(blob);
        }, type, quality);
    });
}

function resizeImageFile(file) {
    if (!(file instanceof File) || !/^image\/(jpeg|png|webp)$/i.test(String(file.type || ''))) {
        return Promise.resolve(file);
    }
    var sourceUrl = URL.createObjectURL(file);
    return loadImageElement(sourceUrl)
        .then(function(image) {
            var width = image.naturalWidth || image.width || 0;
            var height = image.naturalHeight || image.height || 0;
            var longestEdge = Math.max(width, height);
            if (!longestEdge || longestEdge <= MAX_CLIENT_IMAGE_DIMENSION || typeof document === 'undefined') {
                return file;
            }
            var scale = MAX_CLIENT_IMAGE_DIMENSION / longestEdge;
            var canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(width * scale));
            canvas.height = Math.max(1, Math.round(height * scale));
            var context = canvas.getContext('2d');
            if (!context) return file;
            context.drawImage(image, 0, 0, canvas.width, canvas.height);
            return canvasToBlob(canvas, 'image/webp', CLIENT_WEBP_QUALITY)
                .then(function(blob) {
                    return new File([blob], replaceFileExtension(file.name, 'webp'), {
                        type: 'image/webp',
                        lastModified: Date.now(),
                    });
                })
                .catch(function() { return file; });
        })
        .catch(function() { return file; })
        .finally(function() { URL.revokeObjectURL(sourceUrl); });
}

function revokeFileEntries(entries) {
    if (!Array.isArray(entries)) return;
    entries.forEach(function(entry) {
        if (entry && entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
    });
}

function prepareUploadFileEntry(file) {
    return resizeImageFile(file)
        .then(function(resizedFile) {
            return {
                file: resizedFile,
                name: resizedFile && resizedFile.name ? resizedFile.name : (file && file.name ? file.name : 'image'),
                previewUrl: URL.createObjectURL(resizedFile),
            };
        });
}

function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

function normalizeCategories(payload) {
    var grouped = payload && typeof payload === 'object' ? payload : {};
    var order = ['pro', 'con', 'general'];
    var groups = [];
    var byId = {};
    order.forEach(function(groupKey) {
        var items = Array.isArray(grouped[groupKey]) ? grouped[groupKey] : [];
        if (!items.length) return;
        var normalizedItems = items.map(function(item) {
            return {
                id: item && item.id != null ? String(item.id) : '',
                slug: item && item.slug ? String(item.slug) : '',
                name: item && item.name ? String(item.name) : '',
                icon: item && item.icon ? String(item.icon) : '',
                sentiment: item && item.sentiment ? String(item.sentiment) : groupKey,
            };
        }).filter(function(item) { return item.id !== ''; });
        if (!normalizedItems.length) return;
        groups.push({ key: groupKey, label: CATEGORY_GROUP_LABELS[groupKey] || groupKey, items: normalizedItems });
        normalizedItems.forEach(function(item) { byId[item.id] = item; });
    });
    return { groups: groups, byId: byId };
}

function readResponsePayload(response, fallbackMessage) {
    return response.json()
        .catch(function() { return null; })
        .then(function(payload) {
            if (response.ok) return payload || {};
            var msg = fallbackMessage;
            if (payload) {
                if (typeof payload.message === 'string' && payload.message.trim()) msg = payload.message;
                else if (typeof payload.error === 'string' && payload.error.trim()) msg = payload.error;
            }
            throw new Error(msg);
        });
}

// "53,42890° N, 14,55300° E": decimal comma and hemisphere, as Polish copy writes coordinates.
function formatPointDegrees(point) {
    var lat = Number(point.lat);
    var lng = Number(point.lng);
    function part(value, positive, negative) {
        return Math.abs(value).toFixed(5).replace('.', ',') + '° ' + (value < 0 ? negative : positive);
    }
    return part(lat, 'N', 'S') + ', ' + part(lng, 'E', 'W');
}

export function createSignalsSubmitController(config) {
    var esc = config && typeof config.esc === 'function' ? config.esc : defaultEsc;
    var categoriesData = null;
    var categoriesPromise = null;
    var state = null;
    var hoveredTileKey = null;
    var turnstileConfig = config && config.turnstile && typeof config.turnstile === 'object' ? config.turnstile : {};
    var turnstileMountTimer = null;
    var persistentTurnstileContainer = null;

    function isTurnstileEnabled() {
        return turnstileConfig.enabled === true
            && typeof turnstileConfig.siteKey === 'string'
            && turnstileConfig.siteKey.trim() !== '';
    }

    function clearTurnstileMountTimer() {
        if (turnstileMountTimer !== null) {
            clearTimeout(turnstileMountTimer);
            turnstileMountTimer = null;
        }
    }

    function disposeTurnstileWidget() {
        clearTurnstileMountTimer();
        if (state && state.turnstileWidgetId != null && typeof window !== 'undefined' && window.turnstile && typeof window.turnstile.remove === 'function') {
            try {
                window.turnstile.remove(state.turnstileWidgetId);
            } catch (error) {
                // Ignore widget cleanup failures.
            }
        }
        if (state) state.turnstileWidgetId = null;
        if (persistentTurnstileContainer && persistentTurnstileContainer.parentNode) {
            persistentTurnstileContainer.parentNode.removeChild(persistentTurnstileContainer);
        }
        persistentTurnstileContainer = null;
    }

    function queueTurnstileMount() {
        if (turnstileMountTimer !== null) return;
        turnstileMountTimer = setTimeout(function() {
            turnstileMountTimer = null;
            mountTurnstileWidget();
        }, 250);
    }

    function resetTurnstileWidget() {
        if (!state) return;
        state.turnstileToken = '';
        if (state.turnstileWidgetId != null && typeof window !== 'undefined' && window.turnstile && typeof window.turnstile.reset === 'function') {
            try { window.turnstile.reset(state.turnstileWidgetId); } catch (error) {}
        }
    }

    function setTurnstileFieldError(message) {
        if (!state) return;
        if (!state.fieldErrors) state.fieldErrors = {};
        state.fieldErrors.turnstile = message;
    }

    function mountTurnstileWidget() {
        if (!state || !isTurnstileEnabled() || state.step !== STEP_FORM) return;
        if (state.turnstileWidgetId != null) return;
        if (!persistentTurnstileContainer) return;
        var container = persistentTurnstileContainer;

        if (state.turnstileUnavailable) {
            container.innerHTML = '<div class="ms-field__hint nm-field-hint">Nie udało się załadować zabezpieczenia formularza.</div>';
            return;
        }

        if (typeof window === 'undefined' || !window.turnstile || typeof window.turnstile.render !== 'function') {
            state.turnstilePending = true;
            container.innerHTML = '<div class="ms-field__hint nm-field-hint">Ładowanie zabezpieczenia formularza…</div>';
            queueTurnstileMount();
            return;
        }

        clearTurnstileMountTimer();
        state.turnstilePending = false;
        container.innerHTML = '';

        try {
            state.turnstileWidgetId = window.turnstile.render(container, {
                sitekey: turnstileConfig.siteKey,
                size: turnstileSize(container),
                callback: function(token) {
                    if (!state) return;
                    state.turnstilePending = false;
                    state.turnstileUnavailable = false;
                    state.turnstileToken = String(token || '');
                    if (state.fieldErrors) delete state.fieldErrors.turnstile;
                },
                'error-callback': function() {
                    if (!state) return;
                    state.turnstilePending = false;
                    state.turnstileUnavailable = true;
                    state.turnstileToken = '';
                    state.turnstileWidgetId = null;
                    setTurnstileFieldError('Nie udało się załadować zabezpieczenia formularza. Odśwież stronę i spróbuj ponownie.');
                    render();
                },
                'expired-callback': function() {
                    if (!state) return;
                    state.turnstileToken = '';
                },
                'timeout-callback': function() {
                    if (!state) return;
                    state.turnstileToken = '';
                },
            });
        } catch (error) {
            state.turnstilePending = false;
            state.turnstileUnavailable = true;
            state.turnstileToken = '';
            state.turnstileWidgetId = null;
            setTurnstileFieldError(error && error.message ? error.message : 'Nie udało się uruchomić zabezpieczenia formularza.');
            render();
        }
    }

    function syncDetachedState() {
        if (!state) return;
        if (config.detailsContentEl.querySelector('[data-signal-submit-form]')) return;
        if (config.clearPointMarker) config.clearPointMarker();
        if (config.clearTileGeometry) config.clearTileGeometry();
        disposeTurnstileWidget();
        revokeFileEntries(state.files);
        state = null;
    }

    function isOpen() {
        syncDetachedState();
        return !!state;
    }

    /* ─── submission footer events ─── */
    var SUBMIT_FOOTER_STEP_ORDER = [STEP_PICK_POINT, STEP_CHOOSE_TARGET, STEP_CHOOSE_CATEGORY, STEP_FORM];

    function emitFooterEvent(name, detail) {
        if (typeof document === 'undefined') return;
        try {
            document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
        } catch (e) {
            var ev = document.createEvent('CustomEvent');
            ev.initCustomEvent(name, true, true, detail || {});
            document.dispatchEvent(ev);
        }
    }

    function isStepValid(step) {
        if (!state) return false;
        switch (step) {
            case STEP_PICK_POINT: return !!state.point;
            case STEP_CHOOSE_TARGET: return !!state.selectedTarget;
            case STEP_CHOOSE_CATEGORY: return state.categoryIds && state.categoryIds.length > 0;
            case STEP_FORM: return !!(state.body && String(state.body).trim().length >= 5 && (isTrustedAuthor() || state.email));
            default: return false;
        }
    }

    function emitStepUpdate() {
        if (!state) return;
        if (state.step === STEP_SUCCESS) {
            emitFooterEvent('signals-submit:end', { reason: 'success' });
            return;
        }
        var idx = SUBMIT_FOOTER_STEP_ORDER.indexOf(state.step);
        if (idx < 0) return;
        emitFooterEvent('signals-submit:step', {
            step: idx,
            total: SUBMIT_FOOTER_STEP_ORDER.length,
            valid: isStepValid(state.step),
            submitting: !!state.submitting,
        });
    }

    function getSelectedCategories() {
        if (!state || !categoriesData || !state.categoryIds.length) return [];
        return state.categoryIds.map(function(id) {
            return categoriesData.byId[String(id)];
        }).filter(Boolean);
    }

    function shouldShowRadiusField() {
        if (!state || !state.selectedTarget) return false;
        return state.selectedTarget.type === 'punkt';
    }

    function getRadiusValue() {
        if (!shouldShowRadiusField()) return 0;
        return clampRadiusValue(state.influenceRadius);
    }

    function clearMessages() {
        if (!state) return;
        state.submitError = '';
        state.submitSuccess = '';
        state.fieldErrors = {};
    }

    function fieldErrorHtml(field) {
        var err = state && state.fieldErrors && state.fieldErrors[field] ? state.fieldErrors[field] : '';
        return err ? '<div class="ms-field__error nm-field-error">' + esc(err) + '</div>' : '';
    }

    function syncPointMarker() {
        if (!state || !state.point || !isFiniteNumber(state.point.lat) || !isFiniteNumber(state.point.lng)) {
            if (config.clearPointMarker) config.clearPointMarker();
            return;
        }
        if (config.showPointMarker) config.showPointMarker(state.point);
    }

    function syncMapPreview() {
        if (!state || !state.point || !isFiniteNumber(state.point.lat) || !isFiniteNumber(state.point.lng)) {
            if (config.clearSignalPreview) config.clearSignalPreview();
            return;
        }

        if (config.showSignalPreview) {
            config.showSignalPreview({
                point: state.point,
                radius: getRadiusValue(),
                target: state.selectedTarget || null,
                submitted: !!state.submitSuccess,
            });
        }
    }

    /* ─── categories ─── */
    function ensureCategoriesLoaded(forceReload) {
        if (categoriesData && !forceReload) return Promise.resolve(categoriesData);
        if (categoriesPromise && !forceReload) return categoriesPromise;

        if (state) { state.categoriesLoading = true; state.categoriesError = ''; render(); }

        categoriesPromise = fetch(config.baseUrl + 'api/signals/categories', { cache: 'no-store' })
            .then(function(r) { return readResponsePayload(r, 'Nie udało się pobrać listy kategorii.'); })
            .then(function(payload) {
                categoriesData = normalizeCategories(payload);
                categoriesPromise = null;
                if (state) { state.categoriesLoading = false; state.categoriesError = ''; render(); }
                return categoriesData;
            })
            .catch(function(error) {
                categoriesPromise = null;
                if (state) { state.categoriesLoading = false; state.categoriesError = error && error.message ? error.message : 'Nie udało się pobrać listy kategorii.'; render(); }
                throw error;
            });
        return categoriesPromise;
    }

    /* ─── PRG locate ─── */
    function fetchPrgLocate(lat, lng) {
        if (!state) return;
        state.prgLoading = true;
        state.prgError = '';
        state.prgTiles = [];
        render();

        fetch(config.baseUrl + 'api/signals/prg-locate?lat=' + encodeURIComponent(lat) + '&lng=' + encodeURIComponent(lng))
            .then(function(r) { return readResponsePayload(r, 'Nie udało się wyszukać obiektów.'); })
            .then(function(tiles) {
                if (!state) return;
                state.prgTiles = Array.isArray(tiles) ? tiles : [];
                state.prgLoading = false;
                render();
            })
            .catch(function(err) {
                if (!state) return;
                state.prgLoading = false;
                state.prgError = err && err.message ? err.message : 'Błąd wyszukiwania.';
                render();
            });
    }

    /* ─── tile geometry preview ─── */
    function drawSelectedTargetGeometry() {
        if (!state || !state.selectedTargetGeometry || !config.showTileGeometry) {
            if (config.clearTileGeometry) config.clearTileGeometry();
            return;
        }

        config.showTileGeometry({
            type: 'Feature',
            geometry: state.selectedTargetGeometry,
            properties: {
                type: state.selectedTarget ? state.selectedTarget.type : '',
                label: state.selectedTarget ? state.selectedTarget.label : '',
                selected: true,
            },
        });
    }

    function showTilePreview(tile) {
        var key = (tile.type || '') + ':' + (tile.id || '');
        if (key === hoveredTileKey) return;
        hoveredTileKey = key;

        if (!tile.geometry || !config.showTileGeometry) {
            drawSelectedTargetGeometry();
            return;
        }

        config.showTileGeometry({
            type: 'Feature',
            geometry: tile.geometry,
            properties: { type: tile.type, label: tile.label },
        });
    }

    function clearTilePreview() {
        hoveredTileKey = null;
        drawSelectedTargetGeometry();
    }

    /* ─── render helpers ─── */
    function buildStepIndicator() {
        var steps = [STEP_PICK_POINT, STEP_CHOOSE_TARGET, STEP_CHOOSE_CATEGORY, STEP_FORM];
        var idx = steps.indexOf(state.step);
        var labels = ['Miejsce', 'Cel', 'Kategoria', 'Opis'];
        return '<div class="nm-wizard-steps">'
            + steps.map(function(s, i) {
                var cls = 'nm-wizard-step';
                if (i < idx) cls += ' nm-wizard-step--done';
                if (s === state.step) cls += ' nm-wizard-step--active';
                return '<div class="' + cls + '">'
                    + '<span class="nm-wizard-step__num">' + (i + 1) + '</span>'
                    + '<span class="nm-wizard-step__label">' + esc(labels[i]) + '</span>'
                    + '</div>';
            }).join('<div class="nm-wizard-step__line"></div>')
            + '</div>';
    }

    function renderStepPickPoint() {
        var pointInfo = state.point && isFiniteNumber(state.point.lat)
            ? '<div class="nm-wizard-point-info"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--nm-accent)" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M12 2v4m0 12v4M2 12h4m12 0h4"/></svg><span>' + esc(state.point.lat.toFixed(6) + ', ' + state.point.lng.toFixed(6)) + '</span></div>'
            : '';
        return ''
            + '<div class="nm-wizard-body">'
            + '  <div class="nm-wizard-pick-prompt">'
            + '    <div class="nm-wizard-pick-icon"><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--nm-accent)" stroke-width="1.5"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg></div>'
            + '    <p class="nm-wizard-pick-text">Kliknij na mapę, aby wskazać miejsce, którego dotyczy Twój sygnał.</p>'
            + '    <button class="ms-button ms-button--secondary ms-button--block nm-secondary-btn" type="button" data-signal-submit-use-center>Użyj środka mapy</button>'
            +      pointInfo
            + '  </div>'
            + '  <div class="nm-wizard-actions">'
            + '    <button class="ms-button ms-button--secondary ms-button--block nm-secondary-btn" type="button" data-signal-submit-cancel>Anuluj</button>'
            + '  </div>'
            + '</div>';
    }

    function buildPrgTilesHtml() {
        if (state.prgLoading) {
            return '<div class="nm-wizard-tiles-loading"><span class="ms-spinner" aria-hidden="true"></span><span>Szukam obiektów w tej lokalizacji…</span></div>';
        }
        if (state.prgError) {
            return '<div class="nm-wizard-tiles-error">' + esc(state.prgError) + '</div>';
        }

        var tiles = [
            { type: 'punkt', label: 'Dokładnie to miejsce', subtitle: state.point ? formatPointDegrees(state.point) : 'Punkt na mapie', icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M12 2v4m0 12v4M2 12h4m12 0h4"/></svg>' },
        ];

        var typeIcons = {
            dzialka: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 7l8-4 8 4v10l-8 4-8-4V7z"/><path d="M4 7l8 4 8-4"/><path d="M12 11v10"/></svg>',
            budynek: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 21V7l8-4 8 4v14"/><path d="M9 21v-6h6v6"/><path d="M9 10h.01M15 10h.01"/><path d="M9 13h.01M15 13h.01"/></svg>',
            gmina: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="3"/></svg>',
            jednostka: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="3"/><line x1="3" y1="12" x2="21" y2="12"/></svg>',
            obreb: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 3h18v18H3z"/><path d="M3 9h18M9 3v18"/></svg>',
            miejscowosc: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 21h18M9 21V7l-4 4M15 21V11l4 4M9 7h6v14"/></svg>',
            plac: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="4" y="4" width="16" height="16" rx="2"/><circle cx="12" cy="12" r="3"/></svg>',
            adres: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>',
            ulica: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 4v16M19 4v16M12 4v4m0 4v4m0 4v0"/></svg>',
        };

        (state.prgTiles || []).forEach(function(tile) {
            tiles.push({
                type: tile.type,
                id: tile.id,
                label: tile.label,
                subtitle: tile.subtitle,
                geometry: tile.geometry || null,
                icon: typeIcons[tile.type] || typeIcons.gmina,
            });
        });

        return '<div class="nm-wizard-tiles">'
            + tiles.map(function(tile, idx) {
                var isActive = state.selectedTarget && state.selectedTarget.type === tile.type && (!tile.id || state.selectedTarget.id === tile.id);
                return '<button type="button" class="nm-wizard-tile' + (isActive ? ' is-active' : '') + '" data-wizard-tile="' + esc(tile.type) + '"' + (tile.id ? ' data-wizard-tile-id="' + esc(String(tile.id)) + '"' : '') + ' data-wizard-tile-idx="' + idx + '">'
                    + '<span class="nm-wizard-tile__icon">' + tile.icon + '</span>'
                    + '<span class="nm-wizard-tile__label">' + esc(tile.label) + '</span>'
                    + '<span class="nm-wizard-tile__sub">' + esc(tile.subtitle) + '</span>'
                    + '</button>';
            }).join('')
            + '</div>';
    }

    function renderStepChooseTarget() {
        // Footer (submission-footer.js) provides Wstecz / Dalej — no inline duplicates.
        return ''
            + '<div class="nm-wizard-body">'
            +   buildPrgTilesHtml()
            + '</div>';
    }

    function buildCategoryTilesHtml() {
        if (state.categoriesLoading) {
            return '<div class="nm-wizard-tiles-loading"><span class="ms-spinner" aria-hidden="true"></span><span>Ładowanie kategorii…</span></div>';
        }
        if (state.categoriesError) {
            return '<div class="nm-wizard-tiles-error">' + esc(state.categoriesError) + '</div>';
        }
        if (!categoriesData || !categoriesData.groups.length) {
            return '<div class="nm-wizard-tiles-error">Brak kategorii.</div>';
        }

        var html = '';
        categoriesData.groups.forEach(function(group) {
            html += '<div class="nm-wizard-cat-group nm-wizard-cat-group--' + esc(group.key) + '">';
            html += '<div class="nm-wizard-cat-group__label">' + esc(group.label) + '</div>';
            html += '<div class="nm-wizard-tiles nm-wizard-tiles--cats">';
            group.items.forEach(function(item) {
                var isActive = state.categoryIds.indexOf(String(item.id)) !== -1;
                html += '<button type="button" class="nm-wizard-tile nm-wizard-tile--cat' + (isActive ? ' is-active' : '') + '" data-wizard-category="' + esc(item.id) + '">'
                    + '<span class="nm-wizard-tile__cat-icon">' + (item.icon || '📌') + '</span>'
                    + '<span class="nm-wizard-tile__label">' + esc(item.name) + '</span>'
                    + '</button>';
            });
            html += '</div></div>';
        });
        return html;
    }

    function renderStepChooseCategory() {
        // Footer provides Wstecz / Dalej.
        return ''
            + '<div class="nm-wizard-body">'
            +   buildCategoryTilesHtml()
            + '</div>';
    }

    function buildFilesHtml() {
        if (!state.files.length && !state.filesLoading) return '';
        return '<div class="nm-signal-submit__file-list">'
            + (state.filesLoading ? '<div class="ms-field__hint nm-field-hint">Przygotowuję zdjęcia…</div>' : '')
            + state.files.map(function(fileEntry, idx) {
                return '<figure class="nm-signal-submit__file-card">'
                    + (fileEntry.previewUrl ? '<img src="' + esc(fileEntry.previewUrl) + '" alt="' + esc(fileEntry.name || 'Podgląd') + '">' : '')
                    + '<button type="button" class="ms-button ms-button--icon ms-button--overlay-icon nm-signal-submit__file-remove" data-wizard-remove-file="' + idx + '" title="Usuń" aria-label="Usuń zdjęcie"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button>'
                    + '</figure>';
            }).join('')
            + '</div>';
    }

    function buildRadiusSlider() {
        var value = getRadiusValue() || 300;
        var progress = Math.max(0, Math.min(100, ((value - MIN_RADIUS) / (MAX_RADIUS - MIN_RADIUS)) * 100));
        return '<div class="nm-field">'
            + '<span>Promień oddziaływania</span>'
            + '<div class="nm-wizard-slider" style="--nm-range-progress:' + progress.toFixed(2) + '%">'
            + '  <div class="nm-wizard-slider__track-wrap">'
            + '    <input data-signal-submit-radius type="range" min="' + MIN_RADIUS + '" max="' + MAX_RADIUS + '" step="' + RADIUS_STEP + '" value="' + value + '">'
            + '    <div class="nm-wizard-slider__scale"><span>' + MIN_RADIUS + ' m</span><span>1 km</span><span>3 km</span><span>5 km</span></div>'
            + '  </div>'
            + '  <label class="nm-wizard-slider__readout">'
            + '    <span class="nm-wizard-slider__val">' + formatRadiusLabel(value) + '</span>'
            + '    <input data-signal-submit-radius-number type="number" min="' + MIN_RADIUS + '" max="' + MAX_RADIUS + '" step="' + RADIUS_STEP + '" value="' + value + '">'
            + '  </label>'
            + '</div>'
            + '<div class="ms-field__hint nm-field-hint">Opcjonalne. Ustaw dokładny zasięg wpływu sygnału co 10 metrów.</div>'
            + '</div>';
    }

    function buildAlertsHtml() {
        if (!state) return '';
        return ''
            + (state.submitSuccess ? '<div class="ms-alert nm-signal-submit__status ms-alert--success nm-signal-submit__status--success">' + esc(state.submitSuccess) + '</div>' : '')
            + (state.submitError ? '<div class="ms-alert nm-signal-submit__status ms-alert--danger nm-signal-submit__status--error">' + esc(state.submitError) + '</div>' : '');
    }

    function renderStepForm() {
        var selectedCategories = getSelectedCategories();
        var showRadius = shouldShowRadiusField();
        var catLabel = selectedCategories.map(function(c) { return (c.icon || '') + ' ' + c.name; }).join(', ') || '–';

        return ''
            + '<div class="nm-wizard-body">'
            + '  <div class="nm-wizard-summary">'
            + '    <div class="nm-wizard-summary__item"><span class="nm-wizard-summary__key">Miejsce</span><span class="nm-wizard-summary__val">' + esc(state.point ? formatPointDegrees(state.point) : 'brak danych') + '</span></div>'
            + '    <div class="nm-wizard-summary__item"><span class="nm-wizard-summary__key">Dotyczy</span><span class="nm-wizard-summary__val">' + esc(state.selectedTarget ? state.selectedTarget.label || state.selectedTarget.type : '–') + '</span></div>'
            + '    <div class="nm-wizard-summary__item"><span class="nm-wizard-summary__key">Kategorie</span><span class="nm-wizard-summary__val">' + esc(catLabel) + '</span></div>'
            + '  </div>'
            +   (showRadius
                ? buildRadiusSlider()
                + '<div data-section="error-radius">' + fieldErrorHtml('radius') + '</div>'
                : '')
            + '  <div class="nm-field">'
            + '    <span>Treść sygnału</span>'
            + '    <textarea data-signal-submit-body rows="5" placeholder="Opisz krótko, co jest ważne w tym miejscu.">' + esc(state.body) + '</textarea>'
            + '    <div data-section="error-body">' + fieldErrorHtml('body') + '</div>'
            + '  </div>'
            + '  <div class="nm-field">'
            + '    <span>Zdjęcia <small>(opcjonalnie, najwyżej 3 zdjęcia JPEG, PNG lub WebP; przeciągnij lub kliknij)</small></span>'
            + '    <div class="nm-wizard-dropzone" data-wizard-dropzone>'
            + '      <input class="nm-wizard-dropzone__input" data-signal-submit-files type="file" accept="image/jpeg,image/png,image/webp" multiple>'
            + '      <div class="nm-wizard-dropzone__prompt">'
            + '        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>'
            + '        <span>Przeciągnij zdjęcia tutaj lub <strong>kliknij</strong></span>'
            + '      </div>'
            + '    </div>'
            + '    <div data-section="files">' + buildFilesHtml() + '</div>'
            + '    <div data-section="error-files">' + fieldErrorHtml('files') + '</div>'
            + '  </div>'
            +   (isTrustedAuthor()
                ? '  <div class="nm-field">'
                + '    <span>Adres e-mail</span>'
                + '    <div class="ms-field__hint nm-field-hint">Sygnał zostanie wysłany z Twojego konta (' + esc(getTrustedAuthorEmail()) + ').</div>'
                + '  </div>'
                : '  <div class="nm-field">'
                + '    <span>Adres e-mail</span>'
                + '    <input data-signal-submit-email type="email" value="' + esc(state.email) + '" placeholder="twoj@email.pl" autocomplete="email">'
                + '    <div class="ms-field__hint nm-field-hint">Wyślemy link do potwierdzenia i edycji.</div>'
                + '    <div data-section="error-email">' + fieldErrorHtml('email') + '</div>'
                + '  </div>')
            +   (isTurnstileEnabled()
                ? '  <div class="nm-field">'
                + '    <span>Weryfikacja</span>'
                + '    <div data-signal-submit-turnstile-anchor></div>'
                + '    <div class="ms-field__hint nm-field-hint">Potwierdź, że wysyłasz sygnał jako człowiek.</div>'
                + '    <div data-section="error-turnstile">' + fieldErrorHtml('turnstile') + '</div>'
                + '  </div>'
                : '')
            + '  <div data-section="alerts">' + buildAlertsHtml() + '</div>'
            + '</div>';
    }

    function renderStepSuccess() {
        return ''
            + '<div class="nm-wizard-body">'
            + '  <div class="nm-signal-submit__success">'
            + '    <div class="nm-signal-submit__success-icon">'
            + '      <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">'
            + '        <circle cx="12" cy="12" r="10"/>'
            + '        <path d="M8 12l2.5 2.5L16 9"/>'
            + '      </svg>'
            + '    </div>'
            + '    <h4 class="nm-signal-submit__success-title">Dziękujemy za Twój sygnał!</h4>'
            + '    <p class="nm-signal-submit__success-text">' + esc(state.submitSuccess || 'Sprawdź skrzynkę e-mail i kliknij link potwierdzający, aby rozpocząć moderację zgłoszenia.') + '</p>'
            + '  </div>'
            + '  <div class="nm-wizard-actions">'
            + '    <button class="ms-button ms-button--secondary ms-button--block nm-secondary-btn" type="button" data-signal-submit-cancel>Zamknij</button>'
            + '    <button class="ms-button ms-button--primary ms-button--block nm-primary-btn" type="button" data-signal-submit-restart>Dodaj kolejny sygnał</button>'
            + '  </div>'
            + '</div>';
    }

    function updateFormStep() {
        if (!state) return;
        var root = config.detailsContentEl;

        var filesSection = root.querySelector('[data-section="files"]');
        if (filesSection) filesSection.innerHTML = buildFilesHtml();

        ['body', 'email', 'files', 'radius', 'turnstile'].forEach(function(field) {
            var el = root.querySelector('[data-section="error-' + field + '"]');
            if (el) el.innerHTML = fieldErrorHtml(field);
        });

        var alerts = root.querySelector('[data-section="alerts"]');
        if (alerts) alerts.innerHTML = buildAlertsHtml();

        var submitBtn = root.querySelector('[data-submit-button]');
        if (submitBtn) {
            submitBtn.disabled = !!state.submitting;
            submitBtn.textContent = state.submitting ? 'Wysyłanie…' : 'Wyślij sygnał';
        }
    }

    function render() {
        if (!state) return;

        if (typeof config.setDetailsMeta === 'function') {
            config.setDetailsMeta('Dodaj uwagę do miejsca');
        } else {
            config.detailsMetaEl.textContent = 'Dodaj uwagę do miejsca';
        }
        config.openNearbyBtn.disabled = true;

        var currentForm = config.detailsContentEl.querySelector('[data-signal-submit-form]');
        var currentStep = currentForm ? currentForm.getAttribute('data-wizard-step') : null;

        if (state.step === STEP_FORM && currentStep === STEP_FORM) {
            updateFormStep();
        } else {
            fullRender();
        }

        syncPointMarker();
        syncMapPreview();
        emitStepUpdate();
    }

    function fullRender() {
        disposeTurnstileWidget();

        var stepHtml;
        switch (state.step) {
            case STEP_PICK_POINT: stepHtml = renderStepPickPoint(); break;
            case STEP_CHOOSE_TARGET: stepHtml = renderStepChooseTarget(); break;
            case STEP_CHOOSE_CATEGORY: stepHtml = renderStepChooseCategory(); break;
            case STEP_FORM: stepHtml = renderStepForm(); break;
            case STEP_SUCCESS: stepHtml = renderStepSuccess(); break;
            default: stepHtml = ''; break;
        }

        var isSuccess = state.step === STEP_SUCCESS;
        var title = isSuccess ? 'Sygnał wysłany' : 'Dodaj uwagę do miejsca';

        config.detailsContentEl.innerHTML = ''
            + '<div class="nm-detail-stack">'
            + '  <form class="ms-card ms-card--grid nm-detail-block nm-signal-submit nm-wizard" data-signal-submit-form data-wizard-step="' + esc(state.step) + '">'
            + '    <div class="nm-section-label">Sygnały społeczności</div>'
            + '    <h3 class="nm-detail-title">' + esc(title) + '</h3>'
            +      (isSuccess ? '' : buildStepIndicator())
            +      stepHtml
            + '  </form>'
            + '</div>';

        if (state.step === STEP_FORM && isTurnstileEnabled()) {
            var anchor = config.detailsContentEl.querySelector('[data-signal-submit-turnstile-anchor]');
            if (anchor) {
                persistentTurnstileContainer = document.createElement('div');
                persistentTurnstileContainer.setAttribute('data-signal-submit-turnstile', '');
                anchor.replaceWith(persistentTurnstileContainer);
                mountTurnstileWidget();
            }
        }
    }

    /* ─── wizard navigation ─── */
    function goToStep(step) {
        if (!state) return;
        state.step = step;

        if (step === STEP_PICK_POINT) {
            state.pointPicking = true;
        } else {
            state.pointPicking = false;
        }

        if (step === STEP_CHOOSE_TARGET && state.point) {
            fetchPrgLocate(state.point.lat, state.point.lng);
        }

        if (step === STEP_CHOOSE_CATEGORY) {
            ensureCategoriesLoaded();
        }

        if (step !== STEP_CHOOSE_TARGET) {
            clearTilePreview();
        }

        render();
        emitStepUpdate();
    }

    function wizardNext() {
        if (!state) return;
        switch (state.step) {
            case STEP_PICK_POINT:
                if (state.point) goToStep(STEP_CHOOSE_TARGET);
                break;
            case STEP_CHOOSE_TARGET:
                if (state.selectedTarget) goToStep(STEP_CHOOSE_CATEGORY);
                break;
            case STEP_CHOOSE_CATEGORY:
                if (state.categoryIds.length) goToStep(STEP_FORM);
                break;
        }
    }

    function wizardBack() {
        if (!state) return;
        switch (state.step) {
            case STEP_CHOOSE_TARGET: goToStep(STEP_PICK_POINT); break;
            case STEP_CHOOSE_CATEGORY: goToStep(STEP_CHOOSE_TARGET); break;
            case STEP_FORM: goToStep(STEP_CHOOSE_CATEGORY); break;
        }
    }

    /* ─── submit ─── */
    function buildPayload() {
        var payload = new FormData();
        var selectedTarget = state.selectedTarget || null;
        var structuredFieldName = getStructuredTargetFieldName(selectedTarget ? selectedTarget.type : '');

        state.categoryIds.forEach(function(id) {
            payload.append('category_ids[]', String(id));
        });
        if (state.categoryIds.length) {
            payload.append('category_id', String(state.categoryIds[0]));
        }

        payload.append('entity_type', selectedTarget ? selectedTarget.type : 'punkt');
        payload.append('body', String(state.body || '').trim());
        if (!isTrustedAuthor()) {
            payload.append('email', String(state.email || '').trim());
        }

        if (shouldShowRadiusField() && String(state.influenceRadius || '').trim() !== '') {
            payload.append('influence_radius_m', String(getRadiusValue()).trim());
        }

        if (state.point && structuredFieldName === '') {
            payload.append('lat', String(state.point.lat));
            payload.append('lng', String(state.point.lng));
        }

        if (selectedTarget && selectedTarget.id) {
            if (structuredFieldName !== '') {
                payload.append(structuredFieldName, String(selectedTarget.id));
            } else if (selectedTarget.type !== 'punkt') {
                payload.append('prg_type', selectedTarget.type);
                payload.append('prg_id', String(selectedTarget.id));
            }
        }

        state.files.forEach(function(fileEntry) {
            if (!fileEntry || !(fileEntry.file instanceof File)) return;
            payload.append('images[]', fileEntry.file, fileEntry.file.name || fileEntry.name || 'image');
        });

        if (isTurnstileEnabled() && String(state.turnstileToken || '').trim() !== '') {
            payload.append('cf-turnstile-response', String(state.turnstileToken).trim());
        }

        return payload;
    }

    function validateState() {
        var fields = {};
        var general = '';

        if (!state.categoryIds.length) general = 'Wybierz co najmniej jedną kategorię sygnału.';
        else if (!state.point) general = 'Wskaż punkt na mapie.';

        if (!String(state.body || '').trim()) fields.body = 'Treść sygnału jest wymagana.';
        if (!isTrustedAuthor() && !isValidEmail(state.email)) fields.email = 'Podaj prawidłowy adres e-mail.';
        if (state.filesLoading) fields.files = 'Poczekaj, aż zdjęcia zostaną przygotowane.';

        if (isTurnstileEnabled()) {
            if (state.turnstileUnavailable) fields.turnstile = 'Nie udało się załadować zabezpieczenia formularza. Odśwież stronę i spróbuj ponownie.';
            else if (state.turnstilePending) fields.turnstile = 'Poczekaj, aż załaduje się zabezpieczenie formularza.';
            else if (!String(state.turnstileToken || '').trim()) fields.turnstile = 'Potwierdź zabezpieczenie formularza.';
        }

        if (shouldShowRadiusField() && String(state.influenceRadius || '').trim() !== '') {
            var radius = getRadiusValue();
            if (!radius || radius < MIN_RADIUS || radius > MAX_RADIUS) {
                fields.radius = 'Promień oddziaływania: ' + MIN_RADIUS + '–' + formatRadiusLabel(MAX_RADIUS) + '.';
            }
        }

        var hasErrors = general !== '' || Object.keys(fields).length > 0;
        return hasErrors ? { general: general, fields: fields } : null;
    }

    function submit() {
        if (!state || state.submitting) return;
        clearMessages();

        var validation = validateState();
        if (validation) {
            state.submitError = validation.general;
            state.fieldErrors = validation.fields;
            render();
            return;
        }

        var requestState = state;
        requestState.submitting = true;
        render();

        fetch(config.baseUrl + 'api/signals/submit', { method: 'POST', body: buildPayload() })
            .then(function(r) { return readResponsePayload(r, 'Nie udało się wysłać sygnału.'); })
            .then(function(payload) {
                if (state !== requestState) return;
                requestState.submitting = false;
                requestState.submitSuccess = payload && payload.message ? payload.message : 'Sprawdź skrzynkę e-mail i potwierdź zgłoszenie.';
                requestState.submitError = '';
                requestState.fieldErrors = {};
                requestState.files = [];
                requestState.step = STEP_SUCCESS;
                render();
            })
            .catch(function(error) {
                if (state !== requestState) return;
                requestState.submitting = false;
                resetTurnstileWidget();
                requestState.submitError = error && error.message ? error.message : 'Nie udało się wysłać sygnału.';
                render();
            });
    }

    /* ─── map interaction ─── */
    function setPointFromMap(latlng) {
        if (!state) return;
        clearMessages();
        state.point = { lat: latlng.lat, lng: latlng.lng };
        // Auto-advance to target selection
        goToStep(STEP_CHOOSE_TARGET);
    }

    function setPointFromCenter() {
        if (!state) return;
        var center = typeof config.getMapCenter === 'function' ? config.getMapCenter() : null;
        if (!center) return;
        clearMessages();
        state.point = { lat: center.lat, lng: center.lng };
        // Auto-advance to target selection
        goToStep(STEP_CHOOSE_TARGET);
    }

    function handleMapClick(latlng) {
        if (!isOpen() || !state) return false;
        if (state.step === STEP_PICK_POINT || state.pointPicking) {
            setPointFromMap(latlng);
            return true;
        }
        return false;
    }

    function handleFilesSelection(filesList) {
        if (!state) return;
        var requestState = state;
        var selectedFiles = Array.prototype.slice.call(filesList || []).filter(function(file) {
            return file instanceof File;
        });

        clearMessages();

        if (state.files.length + selectedFiles.length > MAX_ATTACHMENTS) {
            requestState.submitError = 'Możesz dodać maksymalnie ' + MAX_ATTACHMENTS + ' zdjęcia.';
            render();
            return;
        }

        if (!selectedFiles.length) return;

        requestState.filesLoading = true;
        render();

        Promise.all(selectedFiles.map(function(file) { return prepareUploadFileEntry(file); }))
            .then(function(fileEntries) {
                if (state !== requestState) { revokeFileEntries(fileEntries); return; }
                requestState.files = requestState.files.concat(fileEntries);
                requestState.filesLoading = false;
                render();
            })
            .catch(function(error) {
                if (state !== requestState) return;
                requestState.filesLoading = false;
                requestState.submitError = error && error.message ? error.message : 'Nie udało się przygotować zdjęć.';
                render();
            });
    }

    function removeFile(index) {
        if (!state || !state.files[index]) return;
        var entry = state.files[index];
        if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
        state.files.splice(index, 1);
        render();
    }

    /* ─── tile list for hover lookup ─── */
    function getAllTiles() {
        var tiles = [
            { type: 'punkt', label: 'Dokładnie to miejsce', geometry: null },
        ];
        (state.prgTiles || []).forEach(function(tile) {
            tiles.push(tile);
        });
        return tiles;
    }

    /* ─── open / close ─── */
    function open(context) {
        if (state) {
            revokeFileEntries(state.files);
            disposeTurnstileWidget();
        }
        clearTilePreview();

        state = {
            step: STEP_PICK_POINT,
            point: null,
            pointPicking: true,
            selectedTarget: null,
            prgTiles: [],
            prgLoading: false,
            prgError: '',
            categoryIds: [],
            categoriesLoading: false,
            categoriesError: '',
            body: '',
            email: getTrustedAuthorEmail(),
            influenceRadius: '300',
            files: [],
            filesLoading: false,
            submitError: '',
            submitSuccess: '',
            fieldErrors: {},
            submitting: false,
            turnstilePending: false,
            turnstileToken: '',
            turnstileUnavailable: false,
            turnstileWidgetId: null,
        };

        render();
        emitFooterEvent('signals-submit:start', { total: SUBMIT_FOOTER_STEP_ORDER.length });
        emitStepUpdate();
    }

    function close(options) {
        options = options || {};
        if (config.clearPointMarker) config.clearPointMarker();
        if (config.clearSignalPreview) config.clearSignalPreview();
        clearTilePreview();
        disposeTurnstileWidget();
        if (state) revokeFileEntries(state.files);
        state = null;
        emitFooterEvent('signals-submit:end', { reason: options.reason || 'close' });
        if (options.restore !== false && typeof config.onClose === 'function') config.onClose();
    }

    /* ─── event binding ─── */
    function bindEvents() {
        config.detailsContentEl.addEventListener('click', function(event) {
            if (!isOpen() || !state) return;

            var cancelBtn = event.target.closest('[data-signal-submit-cancel]');
            if (cancelBtn) { event.preventDefault(); close(); return; }

            var restartBtn = event.target.closest('[data-signal-submit-restart]');
            if (restartBtn) { event.preventDefault(); open(); return; }

            var nextBtn = event.target.closest('[data-wizard-next]');
            if (nextBtn && !nextBtn.disabled) { event.preventDefault(); wizardNext(); return; }

            var backBtn = event.target.closest('[data-wizard-back]');
            if (backBtn) { event.preventDefault(); wizardBack(); return; }

            var useCenterBtn = event.target.closest('[data-signal-submit-use-center]');
            if (useCenterBtn) { event.preventDefault(); setPointFromCenter(); return; }

            var tile = event.target.closest('[data-wizard-tile]');
            if (tile) {
                event.preventDefault();
                var tileType = tile.getAttribute('data-wizard-tile');
                var tileId = tile.getAttribute('data-wizard-tile-id');
                var tileLabel = tile.querySelector('.nm-wizard-tile__label');
                var tileSub = tile.querySelector('.nm-wizard-tile__sub');
                state.selectedTarget = {
                    type: tileType,
                    id: tileId ? parseInt(tileId, 10) : null,
                    label: tileLabel ? tileLabel.textContent : tileType,
                    subtitle: tileSub ? tileSub.textContent : '',
                };
                render();
                return;
            }

            var catTile = event.target.closest('[data-wizard-category]');
            if (catTile) {
                event.preventDefault();
                var catId = catTile.getAttribute('data-wizard-category');
                var idx = state.categoryIds.indexOf(catId);
                if (idx === -1) {
                    state.categoryIds.push(catId);
                } else {
                    state.categoryIds.splice(idx, 1);
                }
                render();
                return;
            }

            var removeFileBtn = event.target.closest('[data-wizard-remove-file]');
            if (removeFileBtn) {
                event.preventDefault();
                removeFile(parseInt(removeFileBtn.getAttribute('data-wizard-remove-file'), 10));
                return;
            }
        });

        /* tile hover/tap → draw geometry on map */
        config.detailsContentEl.addEventListener('mouseenter', function(event) {
            if (!isOpen() || !state || state.step !== STEP_CHOOSE_TARGET) return;
            var tile = event.target.closest('[data-wizard-tile]');
            if (!tile) return;
            var tileIdx = parseInt(tile.getAttribute('data-wizard-tile-idx'), 10);
            var allTiles = getAllTiles();
            if (allTiles[tileIdx]) showTilePreview(allTiles[tileIdx]);
        }, true);

        config.detailsContentEl.addEventListener('mouseleave', function(event) {
            if (!isOpen() || !state || state.step !== STEP_CHOOSE_TARGET) return;
            var tile = event.target.closest('[data-wizard-tile]');
            if (!tile) return;
            clearTilePreview();
        }, true);

        /* touch support: show on touchstart, no clear on touchend (stays until next touch) */
        config.detailsContentEl.addEventListener('touchstart', function(event) {
            if (!isOpen() || !state || state.step !== STEP_CHOOSE_TARGET) return;
            var tile = event.target.closest('[data-wizard-tile]');
            if (!tile) { clearTilePreview(); return; }
            var tileIdx = parseInt(tile.getAttribute('data-wizard-tile-idx'), 10);
            var allTiles = getAllTiles();
            if (allTiles[tileIdx]) showTilePreview(allTiles[tileIdx]);
        }, { passive: true });

        config.detailsContentEl.addEventListener('input', function(event) {
            if (!isOpen() || !state) return;

            var bodyInput = event.target.closest('[data-signal-submit-body]');
            if (bodyInput) { state.body = String(bodyInput.value || ''); clearMessages(); emitStepUpdate(); return; }

            var emailInput = event.target.closest('[data-signal-submit-email]');
            if (emailInput) { state.email = String(emailInput.value || ''); clearMessages(); emitStepUpdate(); return; }

            var radiusInput = event.target.closest('[data-signal-submit-radius]');
            if (radiusInput) {
                var normalized = normalizeRadiusValue(radiusInput.value);
                state.influenceRadius = normalized;
                clearMessages();
                var valEl = radiusInput.parentElement && radiusInput.parentElement.querySelector('.nm-wizard-slider__val');
                var numericInput = config.detailsContentEl.querySelector('[data-signal-submit-radius-number]');
                var sliderWrap = radiusInput.parentElement && radiusInput.parentElement.parentElement;
                var radiusValue = clampRadiusValue(normalized);
                if (valEl) valEl.textContent = formatRadiusLabel(radiusValue || 0);
                if (numericInput && numericInput !== radiusInput) numericInput.value = radiusValue || '';
                if (sliderWrap) {
                    sliderWrap.style.setProperty('--nm-range-progress', Math.max(0, Math.min(100, ((radiusValue - MIN_RADIUS) / (MAX_RADIUS - MIN_RADIUS)) * 100)) + '%');
                }
                syncMapPreview();
                return;
            }

            var radiusNumberInput = event.target.closest('[data-signal-submit-radius-number]');
            if (radiusNumberInput) {
                state.influenceRadius = String(radiusNumberInput.value || '');
                clearMessages();
                var sliderInput = config.detailsContentEl.querySelector('[data-signal-submit-radius]');
                var sliderValueEl = config.detailsContentEl.querySelector('.nm-wizard-slider__val');
                var clamped = clampRadiusValue(radiusNumberInput.value);
                if (sliderInput) sliderInput.value = clamped || MIN_RADIUS;
                if (sliderValueEl) sliderValueEl.textContent = formatRadiusLabel(clamped || 0);
                if (sliderInput && sliderInput.parentElement && sliderInput.parentElement.parentElement) {
                    sliderInput.parentElement.parentElement.style.setProperty('--nm-range-progress', Math.max(0, Math.min(100, ((clamped - MIN_RADIUS) / (MAX_RADIUS - MIN_RADIUS)) * 100)) + '%');
                }
                syncMapPreview();
            }
        });

        config.detailsContentEl.addEventListener('change', function(event) {
            if (!isOpen() || !state) return;

            var filesInput = event.target.closest('[data-signal-submit-files]');
            if (filesInput) {
                handleFilesSelection(filesInput.files || []);
                filesInput.value = '';
            }
        });

        config.detailsContentEl.addEventListener('submit', function(event) {
            var form = event.target.closest('[data-signal-submit-form]');
            if (!form) return;
            event.preventDefault();
            submit();
        });

        config.detailsContentEl.addEventListener('dragover', function(event) {
            var dropzone = event.target.closest('[data-wizard-dropzone]');
            if (!dropzone) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = 'copy';
            dropzone.classList.add('is-dragover');
        });

        config.detailsContentEl.addEventListener('dragleave', function(event) {
            var dropzone = event.target.closest('[data-wizard-dropzone]');
            if (!dropzone) return;
            dropzone.classList.remove('is-dragover');
        });

        config.detailsContentEl.addEventListener('drop', function(event) {
            var dropzone = event.target.closest('[data-wizard-dropzone]');
            if (!dropzone) return;
            event.preventDefault();
            dropzone.classList.remove('is-dragover');
            if (event.dataTransfer && event.dataTransfer.files) {
                handleFilesSelection(event.dataTransfer.files);
            }
        });
    }

    bindEvents();

    if (typeof document !== 'undefined') {
        document.addEventListener('signals-submit:prev', function () { if (isOpen()) wizardBack(); });
        document.addEventListener('signals-submit:next', function () { if (isOpen()) wizardNext(); });
        document.addEventListener('signals-submit:submit', function () { if (isOpen()) submit(); });
    }

    return {
        close: close,
        handleMapClick: handleMapClick,
        isOpen: isOpen,
        open: open,
        wizardNext: wizardNext,
        wizardBack: wizardBack,
        submit: submit,
    };
}
